import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import isArray from 'lodash-es/isArray';
import isEqual from 'lodash-es/isEqual';
import keys from 'lodash-es/keys';
import sortBy from 'lodash-es/sortBy';
import { BehaviorSubject, EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { SidenavItem } from './sidenav-item/sidenav-item.interface';
import { catchError, filter, map, skip, switchMap, take, takeUntil } from 'rxjs/operators';
import { MediaObserver } from '@angular/flex-layout';
import { AuthenticationService } from '../../services/base/authentication.service';
import { User } from '../../interfaces/routering/user';
import { TaskService } from '../../services/tasks/task.service';
import { MenuCounts, PusherUserCountResponse } from '../../interfaces/menu-counts';
import { TaskPanelService } from '../../services/tasks/task-panel.service';
import { TaskWebhookService } from '../../services/tasks/task-webhook.service';
import { BroadcastService } from '../../services/base/broadcast.service';
import { WebSocketResponse } from '../../interfaces/base/web-socket-response';

@Injectable()
export class SidenavService implements OnDestroy {

  mobileBreakpoint: string = 'lt-md';

  private taskPermissions: string[] = [
    'tasks.create',
    'tasks.update',
    'tasks.delete',
    'tasks.reopen',
    'tasks.forward',
    'tasks.print',
    'tasks.handle',
    'tasks.view.all',
    'tasks.reassign',
  ];

  private _openSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.mediaObserver.isActive(this.mobileBreakpoint));
  open$: Observable<boolean> = this._openSubject.asObservable();
  private _modeSubject: BehaviorSubject<'side' | 'over'> = new BehaviorSubject<'side' | 'over'>(this.mediaObserver.isActive(this.mobileBreakpoint) ? 'over' : 'side');
  mode$: Observable<'side' | 'over'> = this._modeSubject.asObservable();
  private _collapsedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  collapsed$: Observable<boolean> = this._collapsedSubject.asObservable();

  private taskCounts: MenuCounts = {
    outside_of_tiles: 0,
    open: 0,
    closed: 0,
    all: 0,
  };
  private shouldRecountTasks: boolean = true;

  private sourceUpdatedSubscription: Subscription = null;

  private taskCount$: Subject<null> = new Subject<null>();
  private destroy$: Subject<any> = new Subject<any>();

  constructor(private router: Router,
              private mediaObserver: MediaObserver,
              private authenticationService: AuthenticationService,
              private taskService: TaskService,
              private taskPanelService: TaskPanelService,
              private taskWebhookService: TaskWebhookService,
              private broadcastService: BroadcastService,
  ) {
    this.router.events.pipe(
      filter<NavigationEnd>((event: NavigationEnd): boolean => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: NavigationEnd): void => {
      this.setCurrentlyOpenByRoute(event.url);

      if (this.mediaObserver.isActive(this.mobileBreakpoint)) {
        // Close Sidenav on Mobile after Route Change
        this._openSubject.next(false);
      }

      // update "new task" menu item url
      if ((event.url.startsWith('/taken') || event.url === '/') && !event.url.endsWith('nieuw')) {
        if (this.items.length) {
          this.items.forEach((item: SidenavItem): void => {
            if (item.uniqueId === 'new-task') {
              item.routeOrFunction = event.url + '/nieuw';
            }
          });
        }
      }
    });

    this.taskPanelService.open$.pipe(
      skip(1)
    ).subscribe((value: boolean): void => {
      if (!value && this.shouldRecountTasks) {
        this.shouldRecountTasks = false;
        this.recountTasks();
      }
    });

    this.mediaObserver.asObservable().pipe(
      map((): boolean => this.mediaObserver.isActive(this.mobileBreakpoint)),
      takeUntil(this.destroy$)
    ).subscribe((isMobile: boolean): void => {
      if (isMobile) {
        this._openSubject.next(false);
        this._modeSubject.next('over');
        this._collapsedSubject.next(false);
      } else {
        this._openSubject.next(true);
        this._modeSubject.next('side');
      }
    });

    this.taskCount$.pipe(
      takeUntil(this.destroy$),
      switchMap((): Observable<void> => {
        return this.authenticationService.user$.pipe(
          takeUntil(this.destroy$),
          take(1),
          switchMap((user: User): Observable<void> => {
            if (user && user.permissions.some((p: string): boolean => this.taskPermissions.includes(p))) {
              return this.taskService.count().pipe(
                takeUntil(this.destroy$),
                map((data: MenuCounts): void => {
                  if (data) {
                    this.taskCounts = data;

                    if (this.items.length) {
                      this._items.next(this.items);
                    }
                  }
                })
              );
            }
          }),
          catchError((): Observable<never> => EMPTY)
        );
      }),
      catchError((): Observable<never> => EMPTY)
    ).subscribe();

    this.authenticationService.user$.pipe(takeUntil(this.destroy$)).subscribe((user: User): void => {
      if (user) {
        if (this.items.length) {
          this._items.next(this.items);
        }

        this.recountTasks();

        if (this.sourceUpdatedSubscription !== null) {
          this.sourceUpdatedSubscription.unsubscribe();
        }
        this.sourceUpdatedSubscription = this.taskService.sourceUpdated.pipe(takeUntil(this.destroy$)).subscribe((): void => {
          if (this.taskPanelService.isOpen) {
            this.shouldRecountTasks = true;
          } else {
            this.recountTasks();
          }
        });

        this.broadcastService.getEventSubject('task', 'count').pipe(takeUntil(this.destroy$))
          .subscribe((data: WebSocketResponse<PusherUserCountResponse[]>): void => {
            const counts: PusherUserCountResponse = data.data.find((d: PusherUserCountResponse): boolean => d.id === user.id);

            if (typeof counts !== 'undefined') {
              this.taskCounts = counts.counts;
            }
          });
      } else {
        if (this.sourceUpdatedSubscription !== null) {
          this.sourceUpdatedSubscription.unsubscribe();
        }
      }
    });

    this.items$.pipe(takeUntil(this.destroy$)).subscribe((): void => {
      this._items.forEach((items: SidenavItem[]): void => {
        items.forEach((item: SidenavItem): void => {
          if (typeof item.badgeItem !== 'undefined') {
            if (item.badgeItem === 'tasks_open' && typeof this.taskCounts.open !== 'undefined') {
              item.badge = (this.taskCounts.open ? this.taskCounts.open + '' : null);
              item.badgeColor = '#7cb342';
            } else if (item.badgeItem === 'tasks_closed' && typeof this.taskCounts.closed !== 'undefined') {
              item.badge = (this.taskCounts.closed ? this.taskCounts.closed + '' : null);
              item.badgeColor = '#7cb342';
            } else if (item.badgeItem === 'tasks_all' && typeof this.taskCounts.all !== 'undefined') {
              item.badge = (this.taskCounts.all ? this.taskCounts.all + '' : null);
              item.badgeColor = '#7cb342';
            }
          }

          if (typeof item.subItems !== 'undefined') {
            item.subItems.forEach((subItem: SidenavItem): void => {
              if (typeof subItem.badgeItem !== 'undefined') {
                if (subItem.badgeItem === 'tasks_open' && typeof this.taskCounts.open !== 'undefined') {
                  subItem.badge = (this.taskCounts.open ? this.taskCounts.open + '' : null);
                  subItem.badgeColor = '#7cb342';
                } else if (subItem.badgeItem === 'tasks_closed' && typeof this.taskCounts.closed !== 'undefined') {
                  subItem.badge = (this.taskCounts.closed ? this.taskCounts.closed + '' : null);
                  subItem.badgeColor = '#7cb342';
                } else if (subItem.badgeItem === 'tasks_all' && typeof this.taskCounts.all !== 'undefined') {
                  subItem.badge = (this.taskCounts.all ? this.taskCounts.all + '' : null);
                  subItem.badgeColor = '#7cb342';
                }
              }
            });
          }
        });
      }).then((): void => {
      });
    });
  }

  /**
   * Sidenav Items
   * @type {BehaviorSubject<SidenavItem[]>}
   * @private
   */
  private _items: BehaviorSubject<SidenavItem[]> = new BehaviorSubject<SidenavItem[]>([]);
  items$: Observable<SidenavItem[]> = this._items.asObservable();

  get items(): SidenavItem[] {
    return this._items.getValue();
  }

  set items(items: SidenavItem[]) {
    this._items.next(items);
  }

  private _itemsBottom: BehaviorSubject<SidenavItem[]> = new BehaviorSubject<SidenavItem[]>([]);
  itemsBottom$: Observable<SidenavItem[]> = this._itemsBottom.asObservable();

  get itemsBottom(): SidenavItem[] {
    return this._itemsBottom.getValue();
  }

  set itemsBottom(items: SidenavItem[]) {
    this._itemsBottom.next(items);
  }

  /**
   * Currently Open
   * @type {BehaviorSubject<SidenavItem[]>}
   * @private
   */
  private _currentlyOpen: BehaviorSubject<SidenavItem[]> = new BehaviorSubject<SidenavItem[]>([]);
  currentlyOpen$: Observable<SidenavItem[]> = this._currentlyOpen.asObservable();

  get currentlyOpen(): SidenavItem[] {
    return this._currentlyOpen.getValue();
  }

  set currentlyOpen(currentlyOpen: SidenavItem[]) {
    this._currentlyOpen.next(currentlyOpen);
  }

  recountTasks(): void {
    this.taskCount$.next();
  }

  open(): void {
    this._openSubject.next(true);
  }

  close(): void {
    this._openSubject.next(false);
  }

  // noinspection JSUnusedGlobalSymbols
  setCollapsed(collapsed: boolean): void {
    this._collapsedSubject.next(collapsed);
  }

  toggleCollapsed(): void {
    this._collapsedSubject.next(!this._collapsedSubject.getValue());
  }

  addItems(items: SidenavItem[]): void {
    items.forEach((item: SidenavItem): void => this.addItem(item));
  }

  addItemsBottom(items: SidenavItem[]): void {
    items.forEach((item: SidenavItem): void => this.addItemBottom(item));
  }

  addItem(item: SidenavItem): void {
    const foundIndex: number = this.items.findIndex((existingItem: SidenavItem): boolean => isEqual(existingItem, item));
    if (foundIndex === -1) {
      this.setParentRecursive(item);

      this.items = [...this.items, item];
    }
  }

  addItemBottom(item: SidenavItem): void {
    const foundIndex: number = this.itemsBottom.findIndex((existingItem: SidenavItem): boolean => isEqual(existingItem, item));
    if (foundIndex === -1) {
      this.setParentRecursive(item);

      this.itemsBottom = [...this.itemsBottom, item];
    }
  }

  toggleItemOpen(item: SidenavItem): void {
    let currentlyOpen: SidenavItem[] = this.currentlyOpen;

    if (this.isOpen(item)) {
      if (currentlyOpen.length > 1) {
        currentlyOpen.length = currentlyOpen.indexOf(item);
      } else {
        currentlyOpen = [];
      }
    } else {
      currentlyOpen = this.getParents(item);
    }

    this.currentlyOpen = currentlyOpen;
  }

  sortRecursive(array: SidenavItem[], propertyName: string): SidenavItem[] {
    const that = this;

    array.forEach(function (item: SidenavItem): void {
      const keyArray: string[] = keys(item);
      keyArray.forEach(function (key: string): void {
        if (isArray(item[key])) {
          item[key] = that.sortRecursive(item[key], propertyName);
        }
      });
    });

    return sortBy(array, propertyName);
  }

  // noinspection JSUnusedGlobalSymbols
  getItemByRoute(route: string): SidenavItem {
    return this.getItemByRouteRecursive(route, this.items);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  private getParents(item: SidenavItem, items: SidenavItem[] = []): SidenavItem[] {
    items.unshift(item);

    if (item.parent) {
      return this.getParents(item.parent, items);
    } else {
      return items;
    }
  }

  public isOpen(item: SidenavItem): boolean {
    return (this.currentlyOpen.indexOf(item) > -1);
  }

  private setCurrentlyOpenByRoute(route: string): void {
    const item: SidenavItem = this.getItemByRouteRecursive(route, this.items);
    let currentlyOpen: SidenavItem[] = [];

    if (item && item.parent) {
      currentlyOpen = this.getParents(item);
    } else if (item) {
      currentlyOpen = [item];
    }

    this.currentlyOpen = currentlyOpen;
  }

  private getItemByRouteRecursive(route: string, collection: SidenavItem[]): SidenavItem | null {
    // Normalize input (remove trailing slash)
    let current: string = route.replace(/\/$/, '');
    let result: SidenavItem | null = null;

    // progressively shorten the route until a match is found
    while (current.length > 0 && !result) {
      result = collection.find(
        (i: SidenavItem): boolean => i.routeOrFunction === current
      );

      if (!result) {
        // search in subItems
        for (const item of collection) {
          if (item && item.subItems && item.subItems.length > 0) {
            const found: SidenavItem = this.getItemByRouteRecursive(current, item.subItems);
            if (found) {
              result = found;
              break;
            }
          }
        }
      }

      if (!result) {
        // strip last path segment
        const lastSlash: number = current.lastIndexOf('/');
        if (lastSlash === -1) {
          break;
        }
        current = current.substring(0, lastSlash);
      }
    }

    return result;
  }

  private setParentRecursive(item: SidenavItem): void {
    if (item.subItems && item.subItems.length > 0) {
      item.subItems.forEach((subItem: SidenavItem): void => {
        subItem.parent = item;
        this.setParentRecursive(subItem);
      });
    }
  }
}
